/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { ForwardedRef } from 'preact/compat';
import type { MutableRef } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  CellOverride,
  ColumnResizeDetail,
  ColumnResizingDetail,
  CurrentCellDetail,
  RowActionDetail,
  SortCriterionDetail,
  TableContextMenuConfig,
  TableHorizontalScrollPosition,
  TableSelectionDetail,
  TableSelectionMode,
  TableVerticalScrollPosition,
  TableViewHandle
} from '#UNSAFE_TableView';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  ActiveCell,
  AppliedColumnWidths,
  ColumnHeaderFooterInfo,
  ColumnResizeInfo,
  OverflowInfo,
  SizingInfo,
  StickyColumnInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { useImperativeHandle, useMemo, useRef, useState } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useContextMenu } from './useContextMenu';
import { useTruncationTooltip } from './useTruncationTooltip';
import { useFocusHandling } from './useFocusHandling';
import { useKeyboardHandling } from './useKeyboardHandling';
import { usePointerHandling } from './usePointerHandling';
import { useScrollHandling } from './useScrollHandling';
import { getElementFromCell } from '../utils/TableDomUtils';
import { getHorizontalScrollPosition, getVerticalScrollPosition } from '../utils/TableScrollUtils';

type UseInteractionManagerOptions<K, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  ref?: ForwardedRef<TableViewHandle<K, C>>;
  tableId: string;
  tableData: CollectionData<K, D>[];
  hasMore: boolean;
  hasFooters: boolean;
  isRtl: boolean;
  columnsArray: TableColumn<K, D, C>[];
  stickyColumnInfo: StickyColumnInfo;
  appliedColumnWidths?: AppliedColumnWidths<C>;
  isLayoutRequired: boolean;
  sizingInfoRef: MutableRef<SizingInfo>;
  overflowInfoRef: MutableRef<OverflowInfo>;
  stickyEdgesState: { start?: number; end?: number };
  setStickyEdgesState: (stickyEdgesState: { start?: number; end?: number }) => void;
  cellHasRenderer: (cell: ActiveCell<K, C>) => boolean;
  horizontalScrollPositionOverride?: TableHorizontalScrollPosition<C>;
  verticalScrollPositionOverride?: TableVerticalScrollPosition<K>;
  onColumnResizing?: (detail: ColumnResizingDetail<C>) => void;
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void;
  currentCellOverride?: CellOverride<K, C>;
  onPersistCurrentCell?: (detail: CurrentCellDetail<K, C>) => void;
  selectionMode?: TableSelectionMode;
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onSelectionChange?: (detail: TableSelectionDetail<K, C>) => void;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  isPendingLayout: boolean;
  isEmbedded: boolean;
  contextMenuConfig?: TableContextMenuConfig<K, D, C>;
};

const _defaultDisabledRowInteractionKeys: ('enter' | 'space')[] = ['enter'];

/**
 * Hook that manages interactions and events on the Preact Table
 */
export function useInteractionManager<K extends string | number, D, C extends string>({
  rootRef,
  viewportInfo,
  ref,
  tableId,
  tableData,
  hasMore,
  hasFooters,
  isRtl,
  columnsArray,
  stickyColumnInfo,
  appliedColumnWidths,
  isLayoutRequired,
  sizingInfoRef,
  overflowInfoRef,
  stickyEdgesState,
  setStickyEdgesState,
  cellHasRenderer,
  horizontalScrollPositionOverride,
  verticalScrollPositionOverride,
  onColumnResizing,
  onColumnResize,
  currentCellOverride,
  onPersistCurrentCell,
  selectionMode,
  selected,
  onSelectionChange,
  onRowAction,
  sortCriterion,
  onSortCriterionChange,
  isPendingLayout,
  isEmbedded,
  contextMenuConfig
}: UseInteractionManagerOptions<K, D, C>) {
  // tracks the current column resize state
  const resizeInfoRef = useRef<ColumnResizeInfo<C>>({
    isResizing: false,
    isResizeHover: false,
    ignoreClick: false
  });

  // tracks the column key for any header or footer being hovered over
  const [activeStyleColumnInfo, setActiveStyleColumnInfo] = useState<ColumnHeaderFooterInfo<C>>();

  // handles default tooltip state management
  const { tooltipContent, tooltipId, tooltipAnchorRef, showTooltip, hideTooltip } =
    useTruncationTooltip<K, C>({ rootRef, tableId, resizeInfoRef });

  // whether row selection is triggered on 'enter' keypresses in addition to 'spacebar'
  const disabledRowInteractionKeys = isEmbedded ? undefined : _defaultDisabledRowInteractionKeys;

  // tracks the current selection extension element (if any)
  const selectionExtensionElementRef = useRef<HTMLElement>();

  // intercept onSelectionChange calls to ensure local selectionExtensionElementRef is cleared
  const onTableSelectionChange = useMemo(() => {
    return onSelectionChange != null
      ? (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => {
          if (!isExtendableSelection) {
            selectionExtensionElementRef.current = undefined;
          }
          onSelectionChange(detail);
        }
      : undefined;
  }, [onSelectionChange]);

  // setup focus tracking using the useFocusHandling hook
  const {
    focusHandlingProps,
    activeCell,
    onActiveCellChanged,
    currentActiveCell,
    currentCell,
    isShowFocusRing,
    isTabbableMode,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge,
    updateDeferredScrollDetail
  } = useFocusHandling({
    rootRef,
    viewportInfo,
    tableId,
    tableData,
    hasMore,
    hasFooters,
    columnsArray,
    stickyColumnInfo,
    sizingInfoRef,
    overflowInfoRef,
    selectionExtensionElementRef,
    isRtl,
    cellSupportsTabbable: cellHasRenderer,
    appliedColumnWidths,
    currentCellOverride,
    onPersistCurrentCell,
    showTooltip,
    hideTooltip,
    isPendingLayout,
    isEmbedded,
    isDataLoaded: !hasMore || tableData.length > 0
  });

  const keyboardHandlingProps = useKeyboardHandling({
    rootRef,
    viewportInfo,
    tableId,
    tableData,
    isRtl,
    isTabbableMode,
    columnsArray,
    sizingInfoRef,
    selectionExtensionElementRef,
    activeCell,
    onActiveCellChanged,
    isDropDownSelection: isEmbedded,
    selectionMode,
    selected,
    onTableSelectionChange,
    activeStyleColumnInfo,
    setActiveStyleColumnInfo,
    onRowAction,
    sortCriterion,
    onSortCriterionChange,
    hideTooltip,
    updateDeferredScrollDetail
  });

  const { hoverStyleColumnInfo, pointerHandlingProps } = usePointerHandling({
    rootRef,
    viewportInfo,
    tableId,
    tableData,
    isRtl,
    columnsArray,
    appliedColumnWidths,
    resizeInfoRef,
    selectionExtensionElementRef,
    onColumnResizing,
    onColumnResize,
    activeCell,
    onActiveCellChanged,
    isDropDownSelection: isEmbedded,
    selectionMode,
    selected,
    onTableSelectionChange,
    activeStyleColumnInfo,
    setActiveStyleColumnInfo,
    onRowAction,
    showTooltip,
    hideTooltip,
    tooltipId
  });

  const scrollHandlingProps = useScrollHandling({
    rootRef,
    viewportInfo,
    tableId,
    isLayoutRequired,
    sizingInfoRef,
    overflowInfoRef,
    columnsArray,
    stickyColumnInfo,
    stickyEdgesState,
    setStickyEdgesState,
    fullColumnWidths: appliedColumnWidths?.fullColumnWidths,
    horizontalScrollPositionOverride,
    verticalScrollPositionOverride,
    tooltipAnchorRef,
    hideTooltip,
    isPendingLayout,
    isRtl
  });

  const {
    contextMenuTriggerProps,
    menuProps,
    contextMenuContext,
    isResizeDialogOpen,
    setIsResizeDialogOpen
  } = useContextMenu<K, D, C>(
    rootRef,
    tableId,
    tableData,
    columnsArray,
    contextMenuConfig,
    sortCriterion,
    onSortCriterionChange
  );

  const interactionManagerProps = !isPendingLayout
    ? mergeProps(
        focusHandlingProps,
        keyboardHandlingProps,
        pointerHandlingProps,
        contextMenuTriggerProps
      )
    : focusHandlingProps;

  // setup imperative handle implementation
  useImperativeHandle(
    ref!,
    () => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      return {
        focus: () => {
          if (rootElement != null) {
            if (currentActiveCell != null) {
              const cellElement = getElementFromCell(rootElement, tableId, currentActiveCell);
              if (cellElement != null) {
                cellElement.focus();
                return;
              }
            }
            // if no current cell element was found, just focus the root element
            rootElement.focus();
          }
        },
        getHorizontalScrollPosition: () => {
          if (
            rootElement == null ||
            scrollerElement == null ||
            isLayoutRequired ||
            appliedColumnWidths == null
          ) {
            return {};
          }
          return getHorizontalScrollPosition<K, D, C>(
            rootElement,
            scrollerElement,
            viewportInfo,
            sizingInfoRef.current,
            overflowInfoRef.current,
            columnsArray,
            appliedColumnWidths.fullColumnWidths,
            isRtl
          );
        },
        getVerticalScrollPosition: () => {
          if (rootElement == null || scrollerElement == null || isLayoutRequired) {
            return {};
          }
          return getVerticalScrollPosition<K>(rootElement, scrollerElement, viewportInfo, tableId);
        }
      };
    },
    [
      rootRef,
      viewportInfo,
      tableId,
      columnsArray,
      currentActiveCell,
      isLayoutRequired,
      appliedColumnWidths,
      sizingInfoRef,
      overflowInfoRef,
      isRtl
    ]
  );

  return {
    interactionManagerProps,
    scrollHandlingProps,
    onTableSelectionChange,
    isShowFocusRing,
    isTabbableMode,
    currentCell,
    currentActiveCell,
    resizeInfoRef,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge,
    activeStyleColumnInfo,
    hoverStyleColumnInfo,
    tooltipContent,
    contextMenuContext,
    menuProps,
    isResizeDialogOpen,
    setIsResizeDialogOpen,
    disabledRowInteractionKeys
  };
}
